/**
 * Helpers de presentación de la biblioteca, derivados SIEMPRE del catálogo real
 * (`CatalogBook` / `CatalogKeypoint`). Nada hardcodeado: ediciones, capítulos,
 * niveles y duraciones salen de lo que el `/catalog` expone. Si un dato no
 * existe (p. ej. `durationTargetMin` null), el helper devuelve `null` y la UI
 * decide cómo presentar la ausencia — no inventa un default.
 */

import {
  CEFR_ORDER,
  type CatalogBook,
  type CatalogKeypoint,
  type Cefr,
  type Edition,
} from './types.js';

/** Una referencia a un libro junto a la edición elegida para mostrarlo. */
export interface BookWithEdition {
  book: CatalogBook;
  edition: Edition;
}

/** Ediciones realmente presentes en el libro (no `Partial` vacías). */
export function bookEditions(book: CatalogBook): Edition[] {
  const out: Edition[] = [];
  if (book.editions.short) out.push('short');
  if (book.editions.long) out.push('long');
  return out;
}

/** Edición preferida para una vista compacta: 'short' si existe, si no 'long'. */
export function preferredEdition(book: CatalogBook): Edition {
  const editions = bookEditions(book);
  // Si el libro no declara ediciones, caemos a 'short' como clave nominal
  // (el catálogo siempre trae al menos una; esto sólo evita un undefined).
  return editions.includes('short') ? 'short' : (editions[0] ?? 'short');
}

/** Capítulos (keypoints) de una edición, ordenados por `order`. */
export function bookKeypoints(book: CatalogBook, edition: Edition): CatalogKeypoint[] {
  const keypoints = book.editions[edition]?.keypoints ?? [];
  return [...keypoints].sort((a, b) => a.order - b.order);
}

/**
 * Duración total estimada de una edición, en minutos.
 *
 * OJO: `durationTargetMin` es un campo a NIVEL DE EDICIÓN en el `book.json`
 * (short ≈ 8m, long ≈ 16m) que el catálogo DENORMALIZA igual en cada keypoint.
 * Por eso NO se suma (sumar daría 40/160 para 5/10 capítulos): el total de la
 * edición es ese valor común. Tomamos el máximo declarado (== el valor de la
 * edición); si NINGÚN capítulo lo declara, `null` (la UI omite el "~N min").
 */
export function bookTotalMinutes(book: CatalogBook, edition: Edition): number | null {
  const keypoints = bookKeypoints(book, edition);
  let max: number | null = null;
  for (const kp of keypoints) {
    if (kp.durationTargetMin != null) {
      max = max == null ? kp.durationTargetMin : Math.max(max, kp.durationTargetMin);
    }
  }
  return max != null ? Math.round(max) : null;
}

/** Niveles CEFR con rendition disponible a lo largo de toda una edición. */
export function editionLevels(book: CatalogBook, edition: Edition): Cefr[] {
  const seen = new Set<Cefr>();
  for (const kp of bookKeypoints(book, edition)) {
    for (const lvl of kp.levels) seen.add(lvl);
  }
  return [...seen];
}

/**
 * El nivel de la cuenta (`cefrGlobal`, PROFICIENCIA: puede ser A0) llevado a
 * CONTENIDO de la Audioteca: los libros van de A1 a C2, sin narración A0, así
 * que A0 → A1 (piso de contenido, doc 31 §5). `null` = sin nivel diagnosticado.
 * Un nivel que no es CEFR es un error, no un default.
 */
export function listenLevelOf(cefrGlobal: string): Cefr;
export function listenLevelOf(cefrGlobal: string | null): Cefr | null;
export function listenLevelOf(cefrGlobal: string | null): Cefr | null {
  if (cefrGlobal === null) return null;
  if (cefrGlobal === 'A0') return 'A1';
  const level = CEFR_ORDER.find((c) => c === cefrGlobal);
  if (!level) throw new Error(`listen: nivel de la cuenta desconocido: ${cefrGlobal}`);
  return level;
}

/**
 * Nivel con el que REALMENTE se va a escuchar algo narrado en `levels`, dado el
 * nivel pedido: el pedido si existe, si no el nivel oficial del user, si no el
 * primero que haya. Es el criterio de `openChapter` (ListenProvider), vive acá
 * para que la ficha del libro pueda medir la cobertura AL MISMO nivel que
 * después va a sonar: pidiéndola con la preferencia global a secas, un libro
 * narrado sólo en B2 no mostraba NINGÚN chip a un user en B1 y el player, un
 * tap más tarde, decía 72% para ese mismo capítulo.
 *
 * `levels` vacío devuelve `want` tal cual: no hay nada mejor que ofrecer, y con
 * ese nivel el pedido falla río abajo en vez de inventar un default.
 */
export function effectiveLevel(levels: readonly Cefr[], want: Cefr, userLevel: Cefr | null): Cefr {
  if (levels.includes(want)) return want;
  if (userLevel !== null && levels.includes(userLevel)) return userLevel;
  return levels[0] ?? want;
}
